// components/Admin/ShareQuizModal.tsx
//
// Owner-side sharing controls for one quiz: pick ONE crew (crewId — every
// member can host) and/or toggle direct shares to individual presenters
// (sharedWith/{uid}). Both grant HOSTING only — rules keep edits owner-only.
// Directory + crews load once on open (tiny host-only nodes); the parent
// QuizList already subscribes to /quizzes, so saved changes flow back live.
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Quiz } from '../../types/quiz.types';
import { Crew, PresenterProfile } from '../../types/presenter.types';
import {
  listCrews,
  listPresenterDirectory,
  myCrews,
  setQuizCrew,
  setQuizShare,
} from '../../services/presenter';

interface Props {
  quizId: string;
  quiz: Quiz;
  myUid: string;
  isAdmin: boolean;
  onClose: () => void;
}

export const ShareQuizModal = ({ quizId, quiz, myUid, isAdmin, onClose }: Props) => {
  const [crews, setCrews] = useState<Record<string, Crew>>({});
  const [directory, setDirectory] = useState<Array<{ uid: string } & PresenterProfile>>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listCrews(), listPresenterDirectory()])
      .then(([c, d]) => {
        setCrews(c);
        setDirectory(d);
      })
      .catch(() => toast.error('Could not load crews and presenters.'))
      .finally(() => setLoaded(true));
  }, []);

  // A presenter may only share into crews they belong to (rules enforce it);
  // admins may use any crew.
  const shareableCrews = useMemo(
    () => (isAdmin ? crews : myCrews(crews, myUid)),
    [crews, isAdmin, myUid]
  );

  const presenters = useMemo(() => {
    const q = search.trim().toLowerCase();
    return directory.filter(
      (p) => p.uid !== myUid && (!q || p.name?.toLowerCase().includes(q))
    );
  }, [directory, search, myUid]);

  const handleCrew = async (crewId: string | null) => {
    setBusyKey('crew');
    try {
      await setQuizCrew(quizId, crewId);
    } catch {
      toast.error('Crew share rejected — you must own the quiz and be in that crew.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggleShare = async (uid: string, shared: boolean) => {
    setBusyKey(uid);
    try {
      await setQuizShare(quizId, uid, shared);
    } catch {
      toast.error('Share rejected — only the quiz owner can share it.');
    } finally {
      setBusyKey(null);
    }
  };

  const sharedCount = Object.keys(quiz.sharedWith ?? {}).length;

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="card w-full max-w-md p-6 max-h-[85vh] overflow-y-auto animate-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold font-display tracking-tight">Share quiz</h2>
            <p className="text-sm text-slate-500 truncate">{quiz.title || 'Untitled quiz'}</p>
          </div>
          <button onClick={onClose} className="text-ink/40 hover:text-ink text-xl font-bold leading-none" aria-label="Close">
            ×
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Shared presenters can host this quiz live — they can never edit or delete it.
        </p>

        {!loaded ? (
          <p className="text-slate-400 text-center py-8">Loading…</p>
        ) : (
          <>
            {/* Crew share (one crew per quiz — rules-checkable shape) */}
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mt-5 mb-2">
              Share with a crew
            </p>
            {Object.keys(shareableCrews).length === 0 ? (
              <p className="text-sm text-slate-400">
                You're not in a crew yet — create one under Crews.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(shareableCrews).map(([crewId, crew]) => {
                  const active = quiz.crewId === crewId;
                  return (
                    <button
                      key={crewId}
                      disabled={busyKey === 'crew'}
                      onClick={() => handleCrew(active ? null : crewId)}
                      className={`chip transition ${
                        active ? 'bg-tile-green text-white' : 'bg-ink/10 text-ink hover:bg-ink/15'
                      }`}
                    >
                      {active ? '✓ ' : ''}{crew.name}
                      <span className="opacity-60"> · {Object.keys(crew.members ?? {}).length}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-2">
              One crew per quiz — every member can host it. Tap again to unshare.
            </p>

            {/* Direct shares */}
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mt-5 mb-2">
              Share with a presenter{sharedCount > 0 && ` (${sharedCount} shared)`}
            </p>
            <input
              type="text"
              placeholder="Search presenters…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm mb-2"
            />
            <div className="space-y-1.5">
              {presenters.length === 0 && (
                <p className="text-sm text-slate-400 py-2">No matching presenters.</p>
              )}
              {presenters.slice(0, 20).map((p) => {
                const shared = quiz.sharedWith?.[p.uid] === true;
                return (
                  <div key={p.uid} className="flex items-center gap-2.5 py-1">
                    {p.photoURL ? (
                      <img src={p.photoURL} alt="" className="w-7 h-7 rounded-full ring-1 ring-slate-200 shrink-0" />
                    ) : (
                      <span className="grid place-items-center w-7 h-7 rounded-full bg-slate-200 text-xs font-semibold shrink-0">
                        {(p.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="text-sm font-medium flex-1 truncate">{p.name}</span>
                    <button
                      disabled={busyKey === p.uid}
                      onClick={() => handleToggleShare(p.uid, !shared)}
                      className={`btn text-xs shrink-0 ${
                        shared
                          ? 'text-ink bg-ink/10 hover:bg-ink/15'
                          : 'text-white bg-tile-green hover:bg-tile-green-deep'
                      }`}
                    >
                      {busyKey === p.uid ? '…' : shared ? 'Unshare' : 'Share'}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
